import { useState, useRef, useEffect } from "react";
import { Tooltip } from "primereact/tooltip";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import MyLeaves from "./MyLeaves/MyLeaves";
import {
  LEAVE_DAY_TYPES,
  LEAVE_HALF_DAY_TYPES,
  LEAVES_STATUS_FILTER,
} from "../../../../util/constants";
import { ROLES } from "../../../../util/roleNameConstants";
import TeamLeaves from "./Team Leaves/TeamLeaves";
import { fetchLeaveTypes } from "./MyLeaves/fetchData";
import { Dropdown } from "primereact/dropdown";
import { Menu } from "primereact/menu";
import FilterButton from "../common/FilterButton";
import { MenuItem } from "primereact/menuitem";
import { TabPanel, TabView } from "primereact/tabview";
import { fetchTimeLogUsersList } from "./Team Leaves/fetchData";
import CalendarDateSelector from "../common/CalendarDateSelector";

interface Filters {
  startDate: any;
  endDate: any;
  leaveType?: any;
  user: string | null;
  status: string | null;
}

const LeavesManagement = () => {
  const menu = useRef<Menu>(null);
  const toast = useRef<Toast>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isAddLeaveDialogVisible, setIsAddLeaveDialogVisible] = useState(false);
  const [isViewLeaveData, setIsViewLeaveData] = useState<boolean>(false);
  const [myLeaveFormValues, setMyLeaveFormValues] = useState<any>({
    leaveType: null,
    startDate: null,
    endDate: null,
    totalLeaveDays: null,
    leaveDayType: LEAVE_DAY_TYPES.FULL_DAY,
    halfDayPeriod: LEAVE_HALF_DAY_TYPES.AM, // Add this
    departments: null,
    description: null,
  });
  const [timeLogUsersList, setTimeLogUsersList] = useState<any>([]);

  const [teamLeavesFormValues, setTeamLeavesFormValues] = useState<any>({
    user: null,
    leaveType: null,
    startDate: null,
    endDate: null,
    totalLeaveDays: null,
    leaveDayType: LEAVE_DAY_TYPES.FULL_DAY,
    halfDayPeriod: LEAVE_HALF_DAY_TYPES.AM, // Add this
    departments: null,
    description: null,
  });

  const [filters, setFilters] = useState<Filters>({
    startDate: null,
    endDate: null,
    leaveType: null,
    user: null,
    status: null,
  });
  
  const [leaveTypes, setLeaveTypes] = useState<any>(null);
  const [activeView, setActiveView] = useState<"calendar" | "list">("calendar");

  useEffect(() => {
    fetchLeaveTypes(setLeaveTypes, toast);
    fetchTimeLogUsersList(setTimeLogUsersList, toast);
  }, []);

  const clearFilters = () => {
    setFilters((prevFilter: any) => ({
      ...prevFilter,
      leaveType: null,
      user: null,
      status: null,
    }));
  };

  const items = [
    {
      items: [
        {
          template:
            activeIndex === 1 ? (
              <div className="tw-flex tw-flex-col tw-gap-2 tw-mx-5 tw-w-62 tw-mt-3 tw-mb-3">
                <label htmlFor="name" className="tw-font-semibold">
                  Name
                </label>
                <Dropdown
                  className="tw-h-11 tw-border tw-border-solid tw-border-gray-300 tw-rounded-md tw-w-full placeholder:tw-font-normal filter-dropdown"
                  value={filters?.user ?? null}
                  options={timeLogUsersList} //users list
                  optionLabel="displayName" // Assuming 'roles' contains an array of objects with a 'role' key
                  placeholder="Select Name"
                  onChange={(e) => {
                    setFilters((prevFilters) => ({
                      ...prevFilters,
                      user: e.target.value,
                    }));
                  }}
                  filter
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            ) : (
              <></>
            ),
        },
        {
          template: (
            <>
              <div className="tw-flex tw-flex-col tw-gap-2 tw-mx-5 tw-w-62 tw-mt-3 tw-mb-3">
                <label className="tw-font-medium">Leave Type</label>
                <Dropdown
                  value={filters.leaveType ?? ""}
                  options={leaveTypes}
                  onChange={(e) => {
                    setFilters((prev: any) => ({
                      ...prev,
                      leaveType: e.value,
                    }));
                  }}
                  optionLabel="leaveType"
                  placeholder="Select Leave Type"
                  className="tw-h-11 tw-border tw-border-solid tw-border-gray-300 tw-rounded-md tw-w-full placeholder:tw-font-normal filter-dropdown"
                />
              </div>
            </>
          ),
        },
        {
          template: (
            <div className="tw-flex tw-flex-wrap tw-gap-[20px] tw-justify-start tw-items-center tw-ml-[21px] tw-pt-[12px]">
              {LEAVES_STATUS_FILTER.map((status, index) => {
                return (
                  <div
                    key={status.id}
                    className="tw-flex tw-items-center"
                    onClick={() => {
                      setFilters((prevFilters) => ({
                        ...prevFilters,
                        status: status.value,
                      }));
                    }}
                  >
                    <span
                      className={`a-badge tw-border tw-cursor-pointer ${
                        filters.status === status.value
                          ? "selected-filter-status"
                          : ""
                      }`}
                    >
                      <span
                        className={`${status.dotColour} tw-w-2 tw-h-2 tw-rounded-full`}
                      ></span>
                      <span className="badge-value tw-text-ellipsis tw-overflow-hidden tw-max-w-52">
                        {status.label}
                      </span>
                    </span>
                  </div>
                );
              })}
            </div>
          ),
        },
        {
          template: (
            <div className="tw-flex tw-flex-col tw-gap-2 tw-mx-5 tw-w-62 tw-mt-3 tw-mb-4">
              <Button
                className="tw-flex tw-px-3 tw-py-2 tw-rounded tw-bg-red-500 hover:tw-bg-red-500 tw-font-medium tw-gap-2 tw-text-white"
                onClick={clearFilters} // This function will reset filters
              >
                Clear Filters
              </Button>
            </div>
          ),
        },
      ],
    },
  ];

  return (
    <>
      <Toast ref={toast} />
      <div className="tw-flex tw-gap-4 tw-justify-between tw-items-center tw-w-full tw-p-4 tw-bg-[#F8FAFC]">
        <Tooltip target=".description-tooltip-target" />

        <div className="tw-flex tw-justify-start tw-items-center">
          <Button
            type="button"
            onClick={() => setActiveIndex(0)}
            className={`tw-bg-transparent ${
              activeIndex === 0
                ? "tw-border-0 tw-border-b-2 tw-border-b-solid tw-border-b-[#00AFEE] tw-rounded-none"
                : "tw-text-[#9da39e]"
            }   focus:tw-shadow-none tw-px-4 tw-py-1 tw-text-xl tw-font-semibold`}
          >
            My Leaves
          </Button>
          {(JSON.parse(localStorage.userAssociated) === true ||
            localStorage.roleName.includes(ROLES.TIMELOG_ADMIN)) && (
            <Button
              type="button"
              onClick={() => setActiveIndex(1)}
              className={`tw-bg-transparent ${
                activeIndex === 1
                  ? "tw-border-0 tw-border-b-2 tw-border-b-solid tw-border-b-[#00AFEE] tw-rounded-none"
                  : "tw-text-[#9da39e]"
              }  focus:tw-shadow-none tw-px-4 tw-py-1 tw-text-xl tw-font-semibold`}
            >
              Team Leaves{" "}
            </Button>
          )}
        </div>
        <div
          className={`tw-flex tw-mb-2 tw-gap-2 tw-justify-content-end tw-items-center`}
        >
          {(activeView === "list" || activeIndex === 0) && (
            <CalendarDateSelector setDateRange={setFilters} />
          )}
          <FilterButton menu={menu} />
          <Menu
            className="tw-w-80"
            model={items as MenuItem[]}
            popup
            ref={menu}
          />
          <Button
            className="tw-flex tw-px-3 tw-py-2 tw-rounded bg-primary border-primary tw-text-white tw-font-medium focus:tw-shadow-none hover:tw-bg-[#02a2db]"
            label="Add Leave"
            icon="pi pi-plus"
            onClick={() => {
              setIsAddLeaveDialogVisible(true);
            }}
          />
        </div>
      </div>

      <TabView
        className="time-log-tab tw-w-full"
        activeIndex={activeIndex}
        onTabChange={(e) => setActiveIndex(e.index)}
      >
        <TabPanel header="My Leaves">
          <div>
            <MyLeaves
              isAddLeaveDialogVisible={isAddLeaveDialogVisible}
              setIsAddLeaveDialogVisible={setIsAddLeaveDialogVisible}
              formValues={myLeaveFormValues}
              setFormValues={setMyLeaveFormValues}
              filters={filters}
              setFilters={setFilters}
              leaveTypes={leaveTypes}
            />
          </div>
        </TabPanel>
        <TabPanel header="Time Leaves">
          <div>
            <TeamLeaves
              isAddLeaveDialogVisible={isAddLeaveDialogVisible}
              setIsAddLeaveDialogVisible={setIsAddLeaveDialogVisible}
              formValues={teamLeavesFormValues}
              setFormValues={setTeamLeavesFormValues}
              filters={filters}
              leaveTypes={leaveTypes}
              activeView={activeView}
              setActiveView={setActiveView}
              isViewLeaveData={isViewLeaveData}
              setIsViewLeaveData={setIsViewLeaveData}
            />
          </div>
        </TabPanel>
      </TabView>
    </>
  );
};

export default LeavesManagement;
